import React, { useState } from "react";

import FinProLogo from "../assets/images/logo.png";
import Logout from "../assets/images/logout.svg";
import LogoutHover from "../assets/images/logouthover.svg";
import MenuIcon from "@material-ui/icons/Menu";

import { LOGOUT_ACTION } from "../store/user/actions";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";

import {
  AppBar,
  Toolbar,
  Hidden,
  IconButton,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Drawer,
  Avatar,
  MenuItem,
  Menu,
  AppBarClassKey,
} from "@material-ui/core";

import "./CommonNavbar.scss";

interface Option {
  text: string;
  visible: () => boolean;
  onClick: () => void;
  icon: any;
  className: () => string;
}

const LogoutIcon = (isHovered: boolean) => (
  <img src={isHovered ? LogoutHover : Logout} alt="logout" />
);

const MobileMenu = ({ onClick, onKeyDown, vertical, name, options }: any) => {
  //State
  const [isItemHovered, setItemsHovered] = useState<Array<boolean>>([]);

  //Helpers
  const setItemHovered = (index: number, isHovered: boolean) => {
    let newHoveredItems = [...isItemHovered];
    newHoveredItems[index] = isHovered;
    setItemsHovered(newHoveredItems);
  };

  let menuClassName = "list-menu" + (vertical ? "--vertical" : "--horizontal");
  let itemClassName = "list-item" + (vertical ? "--vertical" : "--horizontal");

  const avatarLetter = !!name ? name.charAt(0).toUpperCase() : "?";

  return (
    <div
      onClick={onClick}
      onKeyDown={onKeyDown}
      className="list-menu-container"
    >
      <List className={menuClassName}>
        {vertical && (
          <>
            <ListItem className="list-icon">
              <Avatar className="avatar--mobile">{avatarLetter}</Avatar>

              <ListItemText className="avatar-name" primary={name} />
            </ListItem>

            <Divider />
          </>
        )}

        {options.map(
          (option: Option, index: number) =>
            option.visible() && (
              <div key={index}>
                {vertical && index === options.length - 1 && <Divider />}

                <ListItem
                  button
                  onClick={option.onClick}
                  onMouseOver={() => setItemHovered(index, true)}
                  onMouseOut={() => setItemHovered(index, false)}
                  className={itemClassName}
                >
                  {option.icon && (
                    <ListItemIcon className="list-icon">
                      {option.icon(isItemHovered[index])}
                    </ListItemIcon>
                  )}

                  <ListItemText
                    primary={option.text}
                    className={option.className()}
                  />
                </ListItem>
              </div>
            )
        )}
      </List>
    </div>
  );
};

const PopoverMenu = ({ anchorEl, ...props }: any) => {
  //State
  const [isItemHovered, setItemsHovered] = useState<Array<boolean>>([]);

  //Hooks
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation();

  //Helpers
  const setItemHovered = (index: number, isHovered: boolean) => {
    let newHoveredItems = [...isItemHovered];
    newHoveredItems[index] = isHovered;
    setItemsHovered(newHoveredItems);
  };

  //Options
  let options : Array<Option> = [
    {
      text: t("Logout"),
      visible: () => true,
      onClick: () => {
        dispatch({ type: LOGOUT_ACTION });
        history.push("/");
      },
      icon: LogoutIcon,
      className: () => "",
    },
  ];

  return (
    <Menu
      id="customized-menu"
      anchorEl={anchorEl}
      keepMounted
      getContentAnchorEl={null}
      anchorReference="anchorEl"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "center",
      }}
      {...props}
    >
      <div className="anchor" />

      {options.map(
        (option, index) =>
          option.visible() && (
            <MenuItem
              key={index}
              onClick={option.onClick}
              onMouseOver={() => setItemHovered(index, true)}
              onMouseOut={() => setItemHovered(index, false)}
            >
              {option.icon && (
                <ListItemIcon className="list-icon">
                  {option.icon(isItemHovered[index])}
                </ListItemIcon>
              )}

              <ListItemText
                primary={option.text}
                className={option.className()}
              />
            </MenuItem>
          )
      )}
    </Menu>
  );
};

interface Props {
  classes: Partial<Record<AppBarClassKey, string>> | undefined;
  name: string;
  pageName: string;
  homeURL: string;
  languageSwitch?: any;
  options: (vertical: boolean) => Array<Option>;
}

export default function CommonNavbar({ classes, name, pageName, homeURL, languageSwitch, options }: Props) {
  //State
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [
    popoverMenuAnchor,
    setPopoverMenuAnchor,
  ] = useState<EventTarget | null>(null);

  //Hooks
  const history = useHistory();

  //Options
  const avatarLetter = name.charAt(0).toUpperCase();

  return (
    <AppBar
      position="sticky"
      color="secondary"
      classes={classes}
    >
      <Toolbar>
        <Hidden only={["xs", "sm"]}>
          <img
            className="logo"
            src={FinProLogo}
            alt="logo"
            onClick={() => history.push(homeURL)}
          />
        </Hidden>

        <Hidden only={["md", "lg", "xl"]}>
          <IconButton
            className="drawer-menu-button"
            edge="start"
            color="inherit"
            aria-label="menu"
            onClick={() => setMobileMenuOpen(true)}
          >
            <MenuIcon />
          </IconButton>
        </Hidden>

        <Hidden only={["md", "lg", "xl"]}>
          <Typography className="page-name" variant="h6">
            {pageName}
          </Typography>
        </Hidden>

        <Hidden only={["md", "lg", "xl"]}>
          <div style={{ width: "100%" }} />
        </Hidden>

        <Hidden only={["xs", "sm"]}>
          <MobileMenu
            options={options(false)}
          />
        </Hidden>

        {languageSwitch}

        <Hidden only={["xs", "sm"]}>
          <Avatar
            className="avatar"
            onClick={(event) => setPopoverMenuAnchor(event.currentTarget)}
          >
            {avatarLetter}
          </Avatar>
          <PopoverMenu
            className="popover-menu"
            anchorEl={popoverMenuAnchor}
            onClose={() => setPopoverMenuAnchor(null)}
            open={!!popoverMenuAnchor}
          />
        </Hidden>
      </Toolbar>

      <Drawer
        style={{ zIndex: 3000 }}
        anchor="left"
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
      >
        <MobileMenu
          vertical
          onClick={() => setMobileMenuOpen(false)}
          onKeyDown={() => setMobileMenuOpen(false)}
          name={name}
          options={options(true)}
        />
      </Drawer>
    </AppBar>
  );
}